import { useCallback } from 'react';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Grid,
  InputAdornment,
  Link,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
// utils
import { uploadApi } from 'src/api/upload';
import useImageCompression from 'src/hooks/useImageCompression';

// components
import { MobileDatePicker } from '@mui/x-date-pickers';
import { useMutation } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { userApi } from 'src/api/user';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useUserContext } from 'src/auth/useUserContext';
import IconPopover from 'src/components/icon-popover/IconPopover';
import Iconify from 'src/components/iconify';
import DobInfoPopover from 'src/sections/auth/DobInfoPopover';
import PhoneNumberInfoPopover from 'src/sections/auth/PhoneNumberInfoPopover';
import i18n from 'src/locales/i18n';
import {
  PHONE_REGEX_OPTIONAL,
  PHONE_VALIDATION_I18N_KEY,
} from 'src/utils/phoneValidation';
import { GetUserDto, UpdateUserDto } from 'woofwoof-api';
import FormProvider, {
  RHFCheckbox,
  RHFTextField,
  RHFPhoneField,
  RHFUploadAvatar,
} from '../../../../components/hook-form';
import { useSnackbar } from '../../../../components/snackbar';
import { translateKey } from 'src/locales/translate';


// ----------------------------------------------------------------------

export type AccountGeneralFormValuesProps = {
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  dob?: string;
  city?: string;
  address?: string;
  zip?: string;
  avatar?: string;
  terms?: boolean;
  marketing?: boolean;
  pwzNumber?: string;
};

type AccountGeneralProps = {
  submitButtonLabel?: string;
  onSubmit?: (values: AccountGeneralFormValuesProps) => void;
  stepValues?: AccountGeneralFormValuesProps | GetUserDto | undefined;
  isStepper?: boolean;
};

export default function AccountGeneral({
  submitButtonLabel = translateKey("common.save"),
  onSubmit,
  stepValues,
  isStepper = false,
}: AccountGeneralProps) {
  const theme = useTheme();
  const { compressImageFile, imageLoading } = useImageCompression();
  const { enqueueSnackbar } = useSnackbar();
  const { user: firebaseUser } = useAuthContext();
  const {
    data,
    refetch,
    isProfessionalRole,
    isOrganizationOwnerRole,
    isOrganizationAdminRole,
  } = useUserContext();

  const isPhoneAuthOnly =
    firebaseUser?.providerData?.every(
      (provider: any) => provider.providerId === 'phone',
    ) ?? false;

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (updateUserDto: UpdateUserDto) =>
      userApi.userControllerUpdateUser({ updateUserDto }),
  });
  const { mutateAsync: onboardUser } = useMutation({
    mutationFn: () => userApi.userControllerOnboardUser(),
  });
  const termsSetToFalse =
    data?.agreements && !(data.agreements as { terms: boolean }).terms;

  const legalTermsHref = isProfessionalRole
    ? 'https://app.vetsi.pl/auth/terms-conditions/professional'
    : 'https://app.vetsi.pl/auth/terms-conditions/user';
  const legalPrivacyHref = isProfessionalRole
    ? 'https://app.vetsi.pl/auth/privacy-policy/professional'
    : 'https://app.vetsi.pl/auth/privacy-policy/user';

  const UpdateUserSchema: Yup.Schema<AccountGeneralFormValuesProps> =
    Yup.object().shape({
      firstName: Yup.string()
        .matches(
          /^[^\d\n\r\t!@#$%^&*()_+=[\]{}|\\:"<>?,/]*$/,
          translateKey("validation.firstNameInvalid"),
        )
        .min(2, translateKey("validation.firstNameMin"))
        .required(translateKey("visit.validationNameRequired")),
      lastName: Yup.string()
        .matches(
          /^[^\d\n\r\t!@#$%^&*()_+=[\]{}|\\:"<>?,/]*$/,
          translateKey("validation.lastNameInvalid"),
        )
        .min(2, translateKey("validation.lastNameMin"))
        .required(translateKey("validation.lastNameRequired")),
      phoneNumber: Yup.string().matches(
        PHONE_REGEX_OPTIONAL,
        () => i18n.t(PHONE_VALIDATION_I18N_KEY),
      ),
      email: Yup.string()
        .email('Podaj poprawny adres email')
        .test('is-unique', translateKey("hardcoded.ten_email_jest_juz_zajety"), async (value) => {
          if (!value || value === (stepValues || data)?.email) return true;
          return true;
        }),
      dob: Yup.string(),
      city: Yup.string(),
      address: Yup.string(),
      zip: Yup.string().matches(/^\d{2}-\d{3}$/, {
        message: 'Wymagany format to XX-XXX',
        excludeEmptyString: true,
      }),
      avatar:
        isProfessionalRole || isOrganizationOwnerRole || isOrganizationAdminRole
          ? Yup.string().required(translateKey("hardcoded.zdjecie_jest_wymagane"))
          : Yup.string(),
      terms: Yup.boolean().when('$termsSetToFalse', (termsSetToFalse, schema) =>
        termsSetToFalse
          ? schema.isTrue(
              translateKey("validation.termsRequired"),
            )
          : schema,
      ),
      marketing: Yup.boolean().when(
        '$termsSetToFalse',
        (termsSetToFalse, schema) =>
          termsSetToFalse ? schema.optional() : schema,
      ),
      pwzNumber: Yup.string(),
    });

  const defaultValues = {
    firstName: (stepValues || data)?.firstName || '',
    lastName: (stepValues || data)?.lastName || '',
    email: (stepValues || data)?.email || '',
    phoneNumber: (stepValues || data)?.phoneNumber || '',
    dob: (stepValues || data)?.dob || '',
    city: (stepValues || data)?.city || '',
    address: (stepValues || data)?.address || '',
    zip: (stepValues || data)?.zip || '',
    avatar: (stepValues || data)?.avatar || '',
    pwzNumber: (stepValues || data)?.pwzNumber || '',
  };

  const agreements = { marketing: false, terms: false };

  const methods = useForm<AccountGeneralFormValuesProps>({
    resolver: yupResolver(UpdateUserSchema, { context: { termsSetToFalse } }),
    defaultValues: termsSetToFalse
      ? { ...defaultValues, ...agreements }
      : defaultValues,
  });

  const { setValue, handleSubmit, watch } = methods;
  const dob = watch('dob');
  const dobValue = dob ? parseISO(dob) : null;

  const handleOnSubmit = async ({
    dob,
    phoneNumber,
    address,
    city,
    zip,
    avatar,
    firstName,
    lastName,
    email,
    terms,
    marketing,
    pwzNumber,
  }: AccountGeneralFormValuesProps) => {
    try {
      if (!data?.onboarded) {
        await onboardUser();
      }
      await mutateAsync({
        firstName,
        lastName,
        email,
        phoneNumber,
        dob,
        address,
        city,
        zip: zip || '',
        avatar,
        terms,
        marketing,
        pwzNumber,
      } as any);
      await refetch();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || error.message || error, {
        variant: 'error',
      });
    }
  };

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        const compressedFile = await compressImageFile(acceptedFiles);
        if (!compressedFile) return;

        const response = await uploadApi.uploadControllerUploadPublic({
          file: compressedFile as File,
        } as any);

        const url = response.data.url;
        setValue('avatar', url, { shouldValidate: true });
      } catch (error) {
        enqueueSnackbar(translateKey("snackbar.accountError"), {
          variant: 'error',
        });
      }
    },
    [setValue, enqueueSnackbar, compressImageFile],
  );

  return (
    <FormProvider
      methods={methods}
      onSubmit={handleSubmit(onSubmit || handleOnSubmit)}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={4} xl={4}>
          <Card
            sx={{
              boxShadow: theme.customShadows.z24,
              px: 3,
              py: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>{translateKey("hardcoded.zdjecie_profilowe")}</Typography>
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                py: 7,
              }}
            >
              {imageLoading ? (
                <Stack
                  sx={{
                    height: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      zIndex: 9999,
                      display: 'flex',
                      borderRadius: '50%',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 144,
                      width: 144,
                      border: `1px dashed ${alpha(
                        theme.palette.grey[500],
                        0.32,
                      )}`,
                    }}
                  >
                    <Box
                      sx={{
                        zIndex: 7,
                        display: 'flex',
                        borderRadius: '50%',
                        position: 'absolute',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 128,
                        height: 128,
                        color: theme.palette.text.disabled,
                        backgroundColor: theme.palette.primary.lighter,
                        transition: theme.transitions.create('opacity', {
                          easing: theme.transitions.easing.easeInOut,
                          duration: theme.transitions.duration.shorter,
                        }),
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 2,
                      mx: 'auto',
                      display: 'block',
                      textAlign: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    Dozwolony format *.jpeg, *.jpg, *.png, *.gif
                  </Typography>
                </Stack>
              ) : (
                <RHFUploadAvatar
                  name="avatar"
                  onDrop={handleDrop}
                  disabled={isLoading}
                  helperText={
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 2,
                          mx: 'auto',
                          display: 'block',
                          textAlign: 'center',
                          color: 'text.secondary',
                        }}
                      >
                        Dozwolony format *.jpeg, *.jpg, *.png, *.gif
                      </Typography>
                    </>
                  }
                />
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={12} lg={8} xl={8}>
          <Card sx={{ p: 3, boxShadow: theme.customShadows.z24 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>{translateKey("hardcoded.informacje_ogolne")}</Typography>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(1, 1fr)',
                md: 'repeat(1, 1fr)',
                lg: 'repeat(2, 1fr)',
              }}
            >
              <RHFTextField
                required
                name="firstName"
                label={translateKey("quickBooking.petNameField")}
                disabled={isLoading}
              />

              <RHFTextField
                required
                name="lastName"
                label={translateKey("auth.fieldLastName")}
                disabled={isLoading}
              />

              <RHFTextField
                name="email"
                label="Adres email"
                disabled={isLoading || !isPhoneAuthOnly}
                helperText={
                  isPhoneAuthOnly && !data?.email
                    ? translateKey("hardcoded.mozesz_dodac_adres_email_aby_otrzymywac_powiadomienia")
                    : !isPhoneAuthOnly
                    ? translateKey("hardcoded.konto_zostalo_zarejestrowane_przy_uzyciu_adresu_email_zmiana")
                    : ''
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconPopover
                        color="#052330"
                        icon={<Iconify icon={'eva:info-outline'} />}
                        title="Wykorzystanie numeru telefonu"
                        body={translateKey("hardcoded.podany_numer_telefonu_nie_bedzie_widoczny_na_klientow")}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              {isProfessionalRole && (
                <RHFTextField
                  name="pwzNumber"
                  label="Numer PWZ"
                  disabled={isLoading}
                  helperText={
                    <Typography variant="caption" color="text.disabled">{translateKey("hardcoded.w_przypadku_posiadania_numeru_pwz_nalezy_go_wprowadzic")}</Typography>
                  }
                />
              )}
              {!isStepper && (
                <MobileDatePicker
                  DialogProps={{
                    sx: (theme) => ({
                      '& .MuiDialogActions-root': { display: 'none' },
                      '& .MuiButtonBase-root.MuiPickersDay-root:focus.Mui-selected':
                        {
                          backgroundColor: theme.palette.primary.main,
                        },
                    }),
                  }}
                  showToolbar={false}
                  label={translateKey("visit.editProfileDob")}
                  closeOnSelect
                  inputFormat="dd/MM/yyyy"
                  disabled={isLoading}
                  value={dobValue}
                  maxDate={new Date()}
                  onChange={(newValue: any) => {
                    if (newValue) {
                      setValue('dob', newValue?.toISOString() || '');
                    }
                  }}
                  renderInput={(params) => (
                    <RHFTextField
                      {...params}
                      name="dob"
                      disabled={isLoading}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <DobInfoPopover />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </Box>
            {!isStepper && (
              <>
                <Typography variant="h6" sx={{ mb: 3, mt: 2 }}>
                  Dane teleadresowe
                </Typography>
                <Box
                  rowGap={3}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(1, 1fr)',
                    md: 'repeat(1, 1fr)',
                    lg: 'repeat(2, 1fr)',
                  }}
                >
                  <RHFPhoneField
                    name="phoneNumber"
                    label={translateKey("auth.fieldPhone")}
                    disabled={isLoading || isPhoneAuthOnly}
                    helperText={
                      isPhoneAuthOnly
                        ? translateKey("hardcoded.konto_zostalo_zarejestrowane_przy_uzyciu_numeru_telefonu_zmiana")
                        : ''
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <PhoneNumberInfoPopover />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <RHFTextField
                    name="city"
                    label="Miasto"
                    disabled={isLoading}
                  />

                  <RHFTextField
                    name="address"
                    label={translateKey("search.book.cardAddress")}
                    disabled={isLoading}
                  />

                  <RHFTextField
                    name="zip"
                    label={translateKey("search.book.zipCode")}
                    disabled={isLoading}
                  />
                </Box>
              </>
            )}

            {termsSetToFalse && (
              <Stack sx={{ paddingLeft: 1, mt: '24px !important' }} spacing={0}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <RHFCheckbox
                    name="terms"
                    label=""
                    disabled={isLoading}
                    customLabel={
                      <Typography variant="body2">{translateKey("hardcoded.akceptuje")}{' '}
                        <Link
                          underline="always"
                          color="text.primary"
                          href={legalTermsHref}
                          target="_blank"
                        >{translateKey("auth.termsRegulations")}</Link>{' '}{translateKey("search.book.termsBetweenLinks")}{' '}
                        <Link
                          underline="always"
                          color="text.primary"
                          href={legalPrivacyHref}
                          target="_blank"
                        >{translateKey("auth.termsPrivacy")}</Link>
                        *
                      </Typography>
                    }
                    sx={{ mr: 1, height: 3 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <RHFCheckbox
                    name="marketing"
                    label=""
                    disabled={isLoading}
                    customLabel={
                      <Typography variant="body2">{translateKey("auth.marketingConsent")}</Typography>
                    }
                    sx={{ mr: 1 }}
                  />
                </Box>
              </Stack>
            )}

            {isProfessionalRole && (
              <Alert severity="info" sx={{ mt: 3 }}>{translateKey("hardcoded.zdjecie_profilowe_jest_wymagane_wybor_odpowiedniej_fotografii_jest")}</Alert>
            )}

            <Stack spacing={3} alignItems="flex-end" sx={{ mt: 3 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isLoading}
              >
                <span>{submitButtonLabel}</span>
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
